import axios, {
  AxiosError,
  type AxiosAdapter,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import {
  endPreviewLocally,
  getPreviewAuth,
  getPreviewCaps,
  getPreviewTombstone,
  reraisePreviewEnded,
} from "@/lib/preview/previewStore";
import { usePreviewBlockStore } from "@/lib/preview/previewBlockStore";
import { useConnectionStore } from "@/lib/connection/connectionStore";
import { tenantBlockedStore } from "@/lib/auth/tenantBlockedStore";
import { currentLocationNext, withNext } from "@/lib/auth/safeNext";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

/**
 * Per-request opt-out of the preview bearer (see the request interceptor).
 * Only ever a REQUEST header the interceptor strips — it never reaches the wire.
 */
export const PREVIEW_BYPASS_HEADER = "X-Preview-Bypass";

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // browser auto-attaches HttpOnly accessToken cookie
  // No default Content-Type: axios sets `application/json` automatically for
  // plain-object bodies and the browser sets `multipart/form-data; boundary=…`
  // automatically for FormData. A hardcoded `application/json` default would
  // break FormData uploads (axios would JSON-stringify the FormData → 415).
});

// 2026-05-16 (audit P2.15): CSRF double-submit. The backend mints a
// non-HttpOnly `csrfToken` cookie on every login + token reissue; the
// cockpit echoes its value in the `X-CSRF-Token` header on every
// mutation. Same-origin policy keeps the cookie value out of reach of
// cross-origin attackers, who therefore can't construct a valid
// header even if SameSite + Origin defences are somehow bypassed.
// GET/HEAD/OPTIONS skipped — they're harmless and pre-flight requests
// would otherwise fail before login.
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);
const CSRF_COOKIE = "csrfToken";

// Exported for non-axios consumers (AiClient's raw-fetch SSE stream) so the
// cookie/CSRF contract lives in exactly one file.
export function readCookie(name: string): string | null {
  if (typeof document === "undefined" || !document.cookie) return null;
  for (const piece of document.cookie.split("; ")) {
    const eq = piece.indexOf("=");
    if (eq === -1) continue;
    const k = piece.substring(0, eq);
    if (k === name) {
      return decodeURIComponent(piece.substring(eq + 1));
    }
  }
  return null;
}

/**
 * A request "targets the previewed event" when it operates on that event's data.
 * Detected three ways so an event-scoped route can't escape the read-only net by
 * keeping the eventId out of the URL path:
 *   1. path segment `/{eventId}`;
 *   2. query param `eventId=<id>` — boundary-guarded so `excludeEventId=<id>`
 *      (a cross-event read) does NOT match and wrongly take the event token;
 *   3. a top-level `eventId` field in the request BODY. Request interceptors run
 *      BEFORE axios serializes the body, so `cfg.data` is still the JS object;
 *      FormData / string bodies have no `.eventId` and don't match.
 * Reads stay faithful either way (the token carries the role's read caps); the
 * only behavioural change is that previously-leaking event writes now 403.
 */
function targetsPreviewEvent(cfg: InternalAxiosRequestConfig, eventId: string): boolean {
  const url = cfg.url ?? "";
  if (url.includes(`/${eventId}`)) return true;
  // eventId is a Mongo ObjectId (hex) — no regex metachars — but escape anyway.
  const eid = eventId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (new RegExp(`[?&]eventId=${eid}(?:&|$)`).test(url)) return true;
  const body: unknown = cfg.data;
  if (
    !!body &&
    typeof body === "object" &&
    !Array.isArray(body) &&
    !(typeof FormData !== "undefined" && body instanceof FormData) &&
    (body as Record<string, unknown>).eventId === eventId
  ) {
    return true;
  }
  return false;
}

/**
 * B2-0 audit r1 (security F1): the refusal of a request to an event whose
 * preview ended UNPLANNED in this tab — shaped like the server's answer to the
 * dead bearer (401 `sessionExpired`), so every caller fails exactly as it did
 * for the request that ended the preview. Never sent: the adapter is not
 * called; `__previewEnded` makes the response interceptor rethrow it untouched
 * (no reissue, no replay, no connection verdict).
 */
function previewEndedRefusal(cfg: InternalAxiosRequestConfig): AxiosError {
  (cfg as RetriableConfig).__previewEnded = true;
  const response: AxiosResponse = {
    status: 401,
    statusText: "Unauthorized",
    data: { errorCode: "sessionExpired" },
    headers: {},
    config: cfg,
  };
  return new AxiosError(
    "The preview of this event ended in this tab — reload the page",
    "ERR_PREVIEW_ENDED",
    cfg,
    undefined,
    response,
  );
}

api.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
  const method = (cfg.method ?? "get").toUpperCase();

  // ── "Preview as role" (read-only) ──────────────────────────────────────
  // When previewing, requests scoped to the previewed event carry the
  // ephemeral preview bearer so the backend answers AS the role: real
  // row/field scope on reads, real capability 403s on writes. The bearer is
  // attached to BOTH GET and POST — this app uses POST for filtered reads
  // (e.g. the paginated guest list), so method is NOT a read/write signal.
  // Read-only is enforced server-side by the token's masked caps (it carries
  // no write capability), exactly as a real read-only custom role behaves.
  // Bearer beats cookie on the backend, so we attach it ONLY to the previewed
  // event's paths — company/auth requests keep cookie auth. Every mint and
  // revoke of the preview itself (start, switch, re-mint, the superseded
  // token's revoke, the stop) rides cookie auth via the bypass flag below — the
  // token routes refuse the preview bearer. (It used to say "because the store
  // flips `active` around them", which held for start and stop only.)
  //
  // Scope detection (see `targetsPreviewEvent`): the eventId is matched in the
  // PATH, the `eventId=<id>` QUERY param, OR a top-level `eventId` in the request
  // BODY — so an event-scoped route can't slip the read-only net by keeping the
  // eventId out of the path (e.g. sender-address writes carry it in the body).
  //
  // ⚠ RESIDUAL: a write that carries the eventId NOWHERE (path, query, or body)
  // — e.g. `DELETE /email-template/{id}`, where the backend resolves the event
  // from the entity — still gets NO bearer → it would ride the admin cookie and
  // MUTATE during a read-only preview, and the response net can't catch it (no
  // bearer → no 403). Those rare routes MUST be blocked at the click via
  // `usePreviewWriteGuard` (see MailTemplates / PCreateMailTemplate delete).
  // Per-request opt-out: an admin action taken WHILE previewing (e.g. "Save to
  // template" from the preview bar) must ride the admin's own cookie auth, not
  // the read-only preview bearer — otherwise it would 403 against the very role
  // being previewed. The sentinel header is stripped here so it never hits the
  // wire (and never trips CORS).
  //
  // B2-0 (PB-FB-H3-PREV-BYPASS): the sentinel is turned into a CONFIG flag
  // before it is deleted. The replays below (`api.request(cfg)` after a 401
  // reissue or a 403 csrfMismatch) run this interceptor again with the same
  // config; with the header gone and no flag they stamped the preview bearer —
  // 403 on every bypass route once the 15-min admin cookie needed a reissue
  // during a 20-min preview. Unknown config keys survive `mergeConfig` like
  // `_retried` does.
  const rc = cfg as RetriableConfig;
  if (cfg.headers && cfg.headers[PREVIEW_BYPASS_HEADER]) {
    rc.__previewBypass = true;
    delete cfg.headers[PREVIEW_BYPASS_HEADER];
  }
  const bypassPreview = rc.__previewBypass === true;

  const preview = getPreviewAuth();
  let bearerAttached = false;
  if (
    !bypassPreview &&
    preview.active &&
    preview.token &&
    preview.eventId &&
    targetsPreviewEvent(cfg, preview.eventId)
  ) {
    cfg.headers = cfg.headers ?? ({} as InternalAxiosRequestConfig["headers"]);
    cfg.headers["Authorization"] = `Bearer ${preview.token}`;
    bearerAttached = true;
    // Mark so the response interceptor knows this 403 came from a preview-scoped
    // request (the url alone can't tell — the eventId may be in body/query).
    rc.__previewScoped = true;
    // …and WHICH token and session it rode, for the 401 rule below. Never the
    // bearer secret itself: configs reach logs and Sentry.
    rc.__previewTokenId = preview.tokenId;
    rc.__previewEpoch = preview.sessionEpoch;
  }
  // Audit r1 (security F1): the preview of this event ended in this tab in a
  // way that BLOCKS it (its bearer died; or any end of a copy tab — see
  // `PreviewTombstone`). Until a reload nothing of this event rides the admin
  // cookie: refused here, the same 401 the dead bearer got. Bypass requests
  // (the token lifecycle, admin actions) pass. Audit r2 (F1): the refusal
  // raises the blocking notice again if nothing shows it any more.
  if (!bypassPreview && !bearerAttached) {
    const ended = getPreviewTombstone();
    if (ended && targetsPreviewEvent(cfg, ended.eventId)) {
      reraisePreviewEnded();
      throw previewEndedRefusal(cfg);
    }
  }

  if (SAFE_METHODS.has(method)) return cfg;
  const token = readCookie(CSRF_COOKIE);
  if (token) {
    // Re-read on every mutation so a fresh login / reissue rotation
    // takes effect without needing to recreate the axios instance.
    cfg.headers = cfg.headers ?? ({} as InternalAxiosRequestConfig["headers"]);
    cfg.headers["X-CSRF-Token"] = token;
  }
  return cfg;
});

/**
 * Connection dot, "busy" leg: count every non-safe request while its adapter
 * call is in flight. Wrapping the ADAPTER (not an interceptor or a transform)
 * is what makes the count leak-proof: `try/finally` balances every outcome —
 * success, HTTP error, network error, cancel, even a synchronous throw inside
 * the adapter (a bad `params` Date → RangeError, which reaches the response
 * handlers WITHOUT a config and could never have been settled by a flag).
 * Everything that fails BEFORE the adapter (an interceptor, a caller's
 * `transformRequest`, an already-aborted signal) never counts. A replay
 * (`api.request(cfg)` after csrfMismatch / token reissue) runs the adapter
 * again and is counted again, sequentially — so the count is never 2 for one
 * logical request.
 *
 * Exported for the client's own tests, which swap the adapter and must keep
 * the counting to test it.
 */
export function withWriteCounting(adapter: AxiosAdapter): AxiosAdapter {
  return async (config) => {
    if (SAFE_METHODS.has((config.method ?? "get").toUpperCase())) {
      return adapter(config);
    }
    const store = useConnectionStore.getState();
    store.writeStarted();
    try {
      return await adapter(config);
    } finally {
      store.writeFinished();
    }
  };
}

api.defaults.adapter = withWriteCounting(axios.getAdapter(api.defaults.adapter));

type RetriableConfig = AxiosRequestConfig & {
  _retried?: boolean;
  /** One-shot guard for the 403 csrfMismatch replay, mirroring `_retried`
   *  on the 401 path so a still-mismatching replay can't loop. */
  _csrfRetried?: boolean;
  /** Set by the request interceptor when the preview bearer was attached, so
   *  the response interceptor can recognize a preview-scoped 403 regardless of
   *  where the eventId lived (path / query / body). */
  __previewScoped?: boolean;
  /** The bypass sentinel, kept as a flag so a replay still bypasses (B2-0). */
  __previewBypass?: boolean;
  /** The preview token id the bearer on this request belongs to (`null` for a
   *  copy tab that hydrated without one — still a value). */
  __previewTokenId?: string | null;
  /** The preview session (`sessionEpoch`) the bearer was attached under. */
  __previewEpoch?: number;
  /** One-shot guard of the 401 heal replay. */
  __previewHealed?: boolean;
  /** Refused client-side: the event's preview ended unplanned (audit r1 F1). */
  __previewEnded?: boolean;
  /** See `VerdictOnlyConfig`. */
  __verdictOnly?: boolean;
};

/**
 * Opt-in for a request whose ONLY purpose is the connection verdict — the
 * watcher's heartbeat. The response error interceptor casts the verdict and
 * rethrows: no CSRF replay, no token reissue, no `mfaRequired` navigation.
 * Why: the reissue awaits a global POST with NO timeout, so a hung reissue
 * endpoint would pin the probe's in-flight guard for good (no heartbeat, no
 * recovery, dot green forever); and a 403 `mfaRequired` must not hard-navigate
 * an IDLE tab out from under a typing operator. Consequence: the heartbeat no
 * longer refreshes an expired access token — the tab's next real request
 * does, as before the wave — and a 401 still counts as "answered".
 * Unknown config keys ride through `mergeConfig` untouched (like `_retried`)
 * and nothing in this file strips them.
 */
export type VerdictOnlyConfig = AxiosRequestConfig & { __verdictOnly?: boolean };

/**
 * "No answer at all" for the connection dot: the browser could not reach a
 * server (DNS, refused, CORS-less gateway page, timeout). Anything WITH a
 * response — 401, 403, 500, an app-emitted 503 — proves the server is there
 * and is deliberately NOT in this set. Non-axios errors (a throw inside
 * `transformRequest` or a request interceptor) carry no verdict either way.
 */
const NO_ANSWER_CODES = new Set(["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"]);
function isNoServerAnswer(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    !error.response &&
    NO_ANSWER_CODES.has(error.code ?? "")
  );
}

/**
 * `rejected` is the server saying no (any HTTP answer) or a throw that is
 * not a transport failure; `unreachable` is the SAME rule as
 * `isNoServerAnswer` — the reissue never got an answer, so nothing is known
 * about the session. The distinction exists for the 401 replay: a network
 * flap during the reissue used to read as "refresh failed" and log the tab
 * out (`veltric:auth-lost` ⇒ `queryClient.clear()`, drafts gone), and the
 * watcher's 10 s red-state probe made that race routine on a flaky link.
 */
export type RefreshOutcome = "ok" | "rejected" | "unreachable";

let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * Exported for the one caller that handles a 401 by hand — the AI SSE client
 * (raw `fetch`, no interceptor) — because it, too, must not read "no answer"
 * as "session gone".
 */
export async function refreshAccessTokenOutcome(): Promise<RefreshOutcome> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      // Browser sends the HttpOnly refreshToken cookie automatically.
      // Backend rotates the refresh JWT and sets fresh access + refresh
      // cookies on the response.
      await axios.post(
        `${API_BASE_URL}/api/v1/authentication/reissue-authentication-token`,
        {},
        { withCredentials: true },
      );
      return "ok";
    } catch (error) {
      return isNoServerAnswer(error) ? "unreachable" : "rejected";
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Boolean contract for the callers that only need "am I signed in": the
 * AuthContext bootstrap and the slotting websocket. `false` covers BOTH
 * refusal and no-answer, and neither of them logs anyone out on it (bootstrap
 * lands on the login page as before; the socket just reports disconnected).
 * Anything that turns `false` into `veltric:auth-lost` must use the outcome.
 */
export async function refreshAccessToken(): Promise<boolean> {
  return (await refreshAccessTokenOutcome()) === "ok";
}

/**
 * Read-only "preview as role" net. A write the previewed role HAS still 403s
 * (the preview token is masked read-only), so instead of leaving each surface
 * to show a generic "no permission" / "unknown error" — or, for builder saves
 * and silent background POSTs, nothing at all — surface the honest read-only
 * notice here, once, for ALL surfaces.
 *
 * Precision: if the backend stamped `missingCapability` on the 403, only pop
 * when that cap IS in the previewed role's full caps (→ a masked write); a cap
 * the role genuinely lacks is a real gap, left to the page's own UI. Before the
 * backend ships that field, fall back to a generic notice on any
 * `insufficientCapability` 403 during preview. The notice store is idempotent,
 * so a burst of 403s (react-query refetch) shows one dialog. Never blocks a
 * request — it only reacts to one the server already refused, so it can't
 * regress the POST-as-read paths the old method-based block once broke.
 */
function maybeSurfacePreviewReadOnly(error: AxiosError) {
  if (!getPreviewAuth().active) return;
  // Only requests the request-interceptor tagged as preview-scoped (bearer
  // attached) can 403 from the read-only token. Using the tag instead of
  // re-deriving from the url catches body/query-eventId routes too.
  if (!(error.config as RetriableConfig | undefined)?.__previewScoped) return;
  const data = error.response?.data as
    | { errorCode?: unknown; missingCapability?: unknown }
    | undefined;
  if (data?.errorCode !== "insufficientCapability") return;
  const cap =
    typeof data.missingCapability === "string" && data.missingCapability.length > 0
      ? data.missingCapability
      : null;
  if (cap) {
    // Precise: a real gap (cap not in the role's full set) is NOT a preview
    // artifact — leave it to the page. Only masked writes pop the notice.
    if (!getPreviewCaps().includes(cap)) return;
    usePreviewBlockStore.getState().show("role");
  } else {
    usePreviewBlockStore.getState().show("generic");
  }
}

/**
 * B2-0: a 401 on a request that carried the preview bearer is about the BEARER
 * — bearer beats cookie server-side — so the cookie reissue + replay below can
 * never fix it and never runs for it:
 *  - preview no longer active, or another session (`sessionEpoch`): rethrow.
 *    No replay, no stop — and the old `Authorization` is never removed, or a
 *    refused write of a read-only preview would replay on the admin cookie;
 *  - the bearer was superseded while the request was out (a re-mint swapped
 *    the token): ONE heal replay with the CURRENT bearer. The heal stamps it
 *    itself — on a replay `cfg.data` is already a JSON string, so a body-eventId
 *    route no longer matches `targetsPreviewEvent` and the interceptor would
 *    leave the old bearer on. A second 401 is rethrown;
 *  - the CURRENT bearer and `sessionExpired`: this token is dead (revoked by
 *    the main tab's re-mint, or expired) — end the preview locally, no replay.
 *    That end is UNPLANNED: it leaves the tombstone (audit r1 F1), so the
 *    flow's NEXT request of this event is refused instead of riding the admin
 *    cookie. Any other code (`otpRequired`…) is rethrown and stops nothing.
 */
function handlePreviewScoped401(error: AxiosError, cfg: RetriableConfig): Promise<AxiosResponse> {
  const pa = getPreviewAuth();
  if (!pa.active || cfg.__previewEpoch !== pa.sessionEpoch) throw error;
  if (cfg.__previewTokenId !== pa.tokenId) {
    if (cfg.__previewHealed || !pa.token) throw error;
    cfg.__previewHealed = true;
    cfg.headers = cfg.headers ?? ({} as InternalAxiosRequestConfig["headers"]);
    (cfg.headers as Record<string, unknown>)["Authorization"] = `Bearer ${pa.token}`;
    cfg.__previewTokenId = pa.tokenId;
    cfg.__previewScoped = true;
    return api.request(cfg);
  }
  const data = error.response?.data as { errorCode?: unknown } | undefined;
  if (!cfg.__previewHealed && data?.errorCode === "sessionExpired") {
    endPreviewLocally("sessionExpired");
  }
  throw error;
}

/** Already inside the auth flow — never bounce a challenge back onto itself. */
function isOnAuthRoute(): boolean {
  return (
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/auth/")
  );
}

api.interceptors.response.use(
  (r) => {
    useConnectionStore.getState().noteServerAnswered();
    return r;
  },
  async (error: AxiosError) => {
    const cfg = error.config as RetriableConfig | undefined;
    // Never sent (the tombstone refusal): nothing to judge, nothing to replay.
    if (cfg?.__previewEnded) throw error;
    // Connection verdict FIRST, before the auth/CSRF replays below. A cancel
    // says nothing about the server.
    if (axios.isCancel(error)) throw error;
    if (isNoServerAnswer(error)) {
      useConnectionStore.getState().noteNetworkFailure();
    } else if (axios.isAxiosError(error) && error.response) {
      useConnectionStore.getState().noteServerAnswered();
    }
    // Verdict cast — a verdict-only request stops here (see `VerdictOnlyConfig`).
    if (cfg?.__verdictOnly) throw error;
    if (error.response?.status === 403) {
      maybeSurfacePreviewReadOnly(error);
      // CSRF double-submit race: the backend rotates the csrfToken cookie
      // on every login + token reissue (CsrfFilter → 403 `csrfMismatch`),
      // so a mutation fired around a reissue — or from a tab that captured
      // the header before another tab re-logged-in — echoes a stale value.
      // The cookie in the jar is already fresh: re-read it and replay ONCE
      // (the request interceptor re-stamps X-CSRF-Token from the cookie).
      const data = error.response.data as { errorCode?: unknown } | undefined;
      // PB-TE-4: company-wide MFA is now enforced per request, so a session
      // that was already open when an admin flipped `requireMfa` starts 403ing
      // `mfaRequired` mid-flight. Before this branch the only handling of
      // mfaRequired was on the LOGIN response, so every surface would have
      // shown a generic error with no way forward. Send the user to enrolment
      // — the same destination login routes them to. /mfa/setup and
      // /mfa/confirm-setup are @MfaExempt server-side, so the flow can
      // complete; the auth-route guard keeps this from looping.
      // The page the user was on rides along as `next` (billing-comms Teil
      // 2a): enrolment returns there instead of to "/". `withNext` validates
      // it (never an auth or portal route) and drops "/".
      if (data?.errorCode === "mfaRequired" && !isOnAuthRoute()) {
        window.location.assign(withNext("/auth/mfa-setup", currentLocationNext()));
        throw error;
      }
      // PB-TBB-1: the tenant gate refuses writes with 403 `tenantBlocked` and a
      // JSON body only — nothing ever wrote the marker cookie the "Account
      // restricted" strip is seeded from, so the strip could not appear. Mark
      // the session here (the next page load reads the cookie) and tell the
      // mounted provider(s) now, so the strip shows without a reload. The 403
      // itself still reaches the caller: the write WAS refused.
      if (data?.errorCode === "tenantBlocked") {
        tenantBlockedStore.setBlocked();
        window.dispatchEvent(new CustomEvent("veltric:tenant-blocked"));
        throw error;
      }
      if (cfg && !cfg._csrfRetried && data?.errorCode === "csrfMismatch") {
        cfg._csrfRetried = true;
        const fresh = readCookie(CSRF_COOKIE);
        if (fresh) {
          cfg.headers = cfg.headers ?? ({} as InternalAxiosRequestConfig["headers"]);
          cfg.headers["X-CSRF-Token"] = fresh;
          return api.request(cfg);
        }
        // PB-CFO-1: no readable csrf cookie at all (expired, cleared, a jar
        // the login never reached). The backend now refuses a session-cookie
        // mutation without it — 403 `csrfMismatch`, nothing minted — and the
        // reissue is the public call that mints a fresh csrf cookie. ONE
        // reissue through the single-flight (global axios, so these
        // interceptors never see it), then ONE replay under the flag set
        // above. A refused or unanswered reissue says nothing about the
        // session here (the 403 was about the csrf cookie, not the access
        // token), so it is NOT `veltric:auth-lost`: the ORIGINAL 403 is
        // rethrown and the caller fails like any other refused write.
        const outcome = await refreshAccessTokenOutcome();
        if (outcome === "unreachable") {
          useConnectionStore.getState().noteNetworkFailure();
          throw error;
        }
        useConnectionStore.getState().noteServerAnswered();
        if (outcome === "rejected") throw error;
        const minted = readCookie(CSRF_COOKIE);
        if (minted) {
          cfg.headers = cfg.headers ?? ({} as InternalAxiosRequestConfig["headers"]);
          cfg.headers["X-CSRF-Token"] = minted;
        }
        return api.request(cfg);
      }
    }
    if (cfg && error.response?.status === 401 && cfg.__previewScoped) {
      return handlePreviewScoped401(error, cfg);
    }
    if (!cfg || error.response?.status !== 401 || cfg._retried) {
      throw error;
    }
    cfg._retried = true;

    // The reissue rides the GLOBAL axios instance, so these interceptors never
    // see it: its verdict is cast here. A reissue with NO answer is a network
    // verdict, not a lost session — the 401 is re-thrown as-is and the caller
    // fails like any other request during an outage; the next real request
    // will 401 and reissue again once the server is back (the watcher's probe
    // is verdict-only and never reissues — see `VerdictOnlyConfig`).
    const outcome = await refreshAccessTokenOutcome();
    if (outcome === "unreachable") {
      useConnectionStore.getState().noteNetworkFailure();
      throw error;
    }
    if (outcome === "rejected") {
      // The 401 above already proved the server is there; a refused reissue
      // is only more of the same evidence.
      useConnectionStore.getState().noteServerAnswered();
      window.dispatchEvent(new CustomEvent("veltric:auth-lost"));
      throw error;
    }
    useConnectionStore.getState().noteServerAnswered();
    return api.request(cfg);
  },
);
